import { useEffect, useRef, useState } from 'react'

export function imageServiceURl(
  img: string,
  height: number | null = null,
  width: string | number | null = null,
  focalPoint: string | null = null,
  blur: boolean = false
) {
  // we are expecting a storyblok image url like this:
  // https://a.storyblok.com/f/171317/1098x600/21b6dac295/about_greg2.webp

  function parseDimensionsFromUrl(url: string) {
    const parts = url.split('/')
    if (parts.length < 6) {
      // gracefully handle the case where the url is not a storyblok image
      return [null, null]
    }

    // check if it can be split first before splitting
    let dimensions = null
    if (parts[5].includes('x')) {
      dimensions = parts[5].split('x')
    }
    if (dimensions && dimensions.length !== 2) {
      throw new Error('URL does not contain valid dimensions')
    }
    if (dimensions) {
      return dimensions.map((dim) => parseInt(dim, 10))
    }
    return [null, null]
  }

  // if there is no image return null
  if (img === null || img === undefined || img === '') {
    return {
      height: 0,
      width: 0,
      imgURL: '',
      currentImageHeight: 0,
      currentImageWidth: 0,
    }
  }

  let filters = null
  const [urlWidth, urlHeight] = parseDimensionsFromUrl(img)
  // if width is null find the width in the url
  if (width === null) {
    width = urlWidth
  }

  // if width is auto set width to 0
  if (width === 'auto') {
    width = 0
  }

  // if the height is null find the height in the url
  if (height === null) {
    height = 0
  }

  // check if the height requested is greater than the height in the url
  // if it is, use the height in the url
  if (urlHeight !== null && height > urlHeight) {
    height = urlHeight
  }

  // build the new URL and enable the image service
  let imgURL = img + '/m/' + width + 'x' + height

  // if the focal point is not null add it to the url
  if (focalPoint !== null) {
    filters = ':focal(' + focalPoint + ')'
  }

  // if blur is true add it to the url
  if (blur) {
    filters = filters + ':blur(10):quality(80)'
  }

  // if filters are set add to the url
  if (filters !== null) {
    imgURL = imgURL + '/filters' + filters
  }

  return {
    height: height,
    width: width,
    imgURL: imgURL,
    currentImageHeight: parseInt(img?.split('/')[5]?.split('x')[1]) || null,
    currentImageWidth: parseInt(img?.split('/')[5]?.split('x')[0]) || null,
  }
}

export function ImageServiceRender(
  url: string,
  alt: string = '',
  focus: string | null = null,
  height: number | null = null,
  width: string | number | null = null,
  imageMap: number[] | null = null,
  disableImageMap: boolean = false
) {
  const imgRef = useRef(null)
  const [srcSet, setSrcSet] = useState<Array<{ src: string; width: number }>>([])
  const [currentImageMap, setCurrentImageMap] = useState<number[] | null>(imageMap)

  useEffect(() => {
    // Reset srcSet when url changes
    setSrcSet([])

    // Set default image map if none provided
    if (currentImageMap === null) {
      setCurrentImageMap([2500, 1800, 1500, 1200, 1050])
    }

    // Generate new srcSet
    const newSrcSet =
      currentImageMap?.map((widthItem) => {
        let imageWidth: string | number | null = width !== null ? width : widthItem
        let imageHeight = height ? height : null

        let imageUrl = imageServiceURl(url, imageHeight, imageWidth, focus)

        // if width is within our mobile range don't add height to the url
        if ((widthItem === 600 || widthItem === 350) && height === null) {
          imageUrl = imageServiceURl(url, 0, imageWidth, focus)
        }

        return {
          src: imageUrl.imgURL,
          width: widthItem,
        }
      }) || []

    setSrcSet(newSrcSet)
  }, [url, height, width, focus, currentImageMap])

  if (disableImageMap) {
    return (
      <picture>
        <img ref={imgRef} src={url + `/m/`} alt={alt} />
      </picture>
    )
  }

  return (
    <picture>
      {srcSet.map((item, index) => (
        <source
          key={`${url}-${item.width}-${index}`}
          srcSet={item.src + ` ${item.width}w`}
          media={`(min-width: ${item.width}px)`}
        />
      ))}
      <img
        ref={imgRef}
        src={
          url +
          `/m/filters:blur(${height && height < 100 ? 2 : 10}):quality(${height && height < 100 ? 95 : 80})`
        }
        alt={alt}
        srcSet={srcSet.map((item) => item.src + ` ${item.width}w`).join(', ')}
      />
    </picture>
  )
}
